<template>
  <div class="fulfillment-methods">
    <template v-if="isShownSkeleton">
      <Skeleton
        class="margin-bottom-xs"
        height="44px"
      />
      <Skeleton height="44px" />
    </template>

    <Fulfillment
      v-else
      :pickup-store="pickupStore"
      :product-info="productInfo"
      :ship-zip-code="zipCode"
      :page="page"
      :boss-enabled="bossEnabled"
      :upc-number="productInfo.upcNumber"
      :fcc-macys-api-key="fccMacysApiKey"
      :enhance-same-day-delivery-enabled="enhanceSameDayDeliveryEnabled"
      :is-international-ship="isInternationalMode"
      :copy-clarity-vue-enabled="copyClarityVueEnabled"
      :free-shipping-eligible="freeShippingEligible"
      :event-badging="eventData"
      :product-category-id="productData?.identifier?.topLevelCategoryID || ''"
      :sdd-promo-enabled="sddPromoEnabled"
      @selected-option="handleSetSelectedRadioValue"
      @current:zipCode="handleUpdateZipCode"
    />
  </div>
</template>

<script setup>
/* istanbul ignore file */
import {
  onMounted, unref, computed, watch, ref, nextTick,
} from 'vue';
import { useMounted } from '@vueuse/core';

import Skeleton from '@common-vue/atomic-ui/src/components/skeleton';
import { Fulfillment } from '@common-vue/fulfillment/src/components/fulfillment';
import { useFeatureEligibility, useKillswitches } from '@modules/featureControl';
import { useProductPrice } from '@modules/price/composables';
import { getLowestPrice } from '@modules/price/parsers';
import { useProduct } from '@modules/product';

import { useEnvProps } from '@composables/useEnvProps';
import { setEventData, getEventData } from '@utils/localstorage/eventBadging';
import { useAvailability } from '../composables/useAvailability';
import { useFulfilmentData } from '../composables/useFulfilmentData';
import { tagAvailabilityRadioChange } from '../../../features/analytics/availability';

const {
  zipCode,
  selectedRadioButton,
  isDataLoadNeededOnMount,
  isDataLoaded,
} = useFulfilmentData();
const {
  parseAvailabilityData,
  pickupStore,
  productInfo,
} = useAvailability();

const { productData } = useProduct();
const {
  fccMacysApiKey, freeShippingCopyClarityPrice, hostlink, brand,
} = useEnvProps();

const isMounted = useMounted();
const { isInternationalMode } = useFeatureEligibility();
const { killswitches } = useKillswitches();
const selectedPrice = useProductPrice();
const freeShippingEligible = ref(false);
const eventData = ref(null);

const page = 'pdp';
const {
  bossEnabled, enhanceSameDayDeliveryEnabled, copyClarityVueEnabled, eventBadgingEnabled, sddPromoEnabled,
} = killswitches;
const isShownSkeleton = computed(() => !isMounted.value || (isDataLoadNeededOnMount && !isDataLoaded.value));

async function fetchEventBadgingData() {
  if (eventBadgingEnabled) {
    const badgingDataFromCache = getEventData();
    if (badgingDataFromCache) {
      await nextTick();
      eventData.value = badgingDataFromCache;
    } else {
      const brandName = brand.toUpperCase();
      const url = `${hostlink}/eventBadging/eventContent${brandName}.json`;
      try {
        let response = await fetch(url);
        response = await response.json();
        await nextTick();
        // @ts-ignore
        eventData.value = response;
        setEventData(response);
      } catch (e) {
      console.error('Error fetching event data:', e) // eslint-disable-line
      }
    }
  }
}

onMounted(() => {
  parseAvailabilityData();
});
/**
 *
 * @param {string} selectedOption
 */
function handleSetSelectedRadioValue(selectedOption) {
  selectedRadioButton.value = selectedOption;

  tagAvailabilityRadioChange({
    radioValue: selectedOption,
    productInfo: unref(productInfo),
    selectedPrice: selectedPrice.value,
  });
}

/**
 * @typedef {object} zipCodeObj
 * @property {import('vue').Ref<string>} currentZipCode
 *
 * @param {zipCodeObj} zipCodeObj - The zip code object.
 */
function handleUpdateZipCode({ currentZipCode }) {
  zipCode.value = currentZipCode.value;
}

/**
 * @param {import("../types").Ship} ship
 */
function freeShippingEligibility(ship) {
  if (['POOL', 'STR1'].includes(ship?.orderType)) {
    const productPrice = getLowestPrice(selectedPrice.value) || 0;
    // @ts-ignore
    return productPrice >= freeShippingCopyClarityPrice;
  }
  return false;
}

watch(productInfo, ({ ship }) => {
  freeShippingEligible.value = freeShippingEligibility(ship);
});

watch(isShownSkeleton, (newVal) => {
  if (!newVal) {
    fetchEventBadgingData();
  }
});
</script>
